import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, MessagesSquare, CalendarPlus, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { trainerChatAPI } from '@/lib/api';
import { ChatVerlauf } from '@/components/TrainerChat';

/* Trainer-Chat im CRM (29.10.2026, Betreiber: „montags die wöchentlichen Termine, die Paul betreuen soll, in seinen
   Kalender eintragen und als gelesen markiert sehen“). Recht leads.edit. */

const BEISPIEL = 'Hi Paul, deine Termine diese Woche:\nMo 10 Max Muster\nDi 17:30-18:30 Anna Schmidt\nDo 18 Uhr 45 Min Check-up Lisa';
const tagDE = (d) => new Date(d + 'T12:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });

export default function TrainerChatPage() {
  const [trainer, setTrainer] = useState(null);
  const [wahl, setWahl] = useState(null);
  const [verlauf, setVerlauf] = useState(null);
  const [text, setText] = useState('');
  const [vorschau, setVorschau] = useState([]);
  const [arbeitet, setArbeitet] = useState(false);

  const ladeListe = useCallback(async () => {
    try { const t = (await trainerChatAPI.uebersicht()).data.trainer || []; setTrainer(t); setWahl(w => w || t.find(x => /paul/i.test(x.name))?.id || t[0]?.id || null); } catch { setTrainer([]); }
  }, []);
  const ladeVerlauf = useCallback(async () => { if (!wahl) return; try { setVerlauf((await trainerChatAPI.verlauf(wahl)).data.nachrichten || []); } catch { setVerlauf([]); } }, [wahl]);
  useEffect(() => { ladeListe(); }, [ladeListe]);
  useEffect(() => { setVerlauf(null); ladeVerlauf(); const t = setInterval(ladeVerlauf, 30000); return () => clearInterval(t); }, [ladeVerlauf]);
  // Vorschau der erkannten Termine, kurz nach dem Tippen
  useEffect(() => {
    if (!wahl || !text.trim()) { setVorschau([]); return undefined; }
    const t = setTimeout(async () => { try { setVorschau((await trainerChatAPI.vorschau(wahl, text)).data.termine || []); } catch { setVorschau([]); } }, 400);
    return () => clearTimeout(t);
  }, [text, wahl]);

  const senden = async () => {
    if (!text.trim()) return;
    const hinweise = vorschau.filter(v => v.hinweis);
    if (hinweise.length && !window.confirm(`${hinweise.map(h => `${h.zeile}: ${h.hinweis}`).join('\n')}\n\nTrotzdem so eintragen?`)) return;
    setArbeitet(true);
    try {
      const r = await trainerChatAPI.senden(wahl, { text: text.trim(), termine_eintragen: true });
      const n = r.data.termine?.length || 0;
      toast.success(n ? `Gesendet · ${n} Termin${n === 1 ? '' : 'e'} eingetragen` : 'Gesendet');
      if (r.data.nicht_eingetragen?.length) toast.error(`${r.data.nicht_eingetragen.length} Zeile(n) nicht eingetragen`);
      setText(''); setVorschau([]); ladeVerlauf(); ladeListe();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Senden fehlgeschlagen'); }
    finally { setArbeitet(false); }
  };

  const name = trainer?.find(t => t.id === wahl)?.name || '';
  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="trainer-chat-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><MessagesSquare className="w-6 h-6 text-marke" /> Trainer-Chat</h1>
        <p className="text-sm text-muted-foreground">Schreib die Termine der Woche je Zeile („Mo 10 Max Muster“) — sie landen im Kalender des Trainers. „Gelesen“ siehst du unter jeder Nachricht.</p>
      </div>
      {trainer === null ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : (
        <div className="grid md:grid-cols-[220px_1fr] gap-4">
          <Card className="border-border/60"><CardContent className="p-2 space-y-1">
            {trainer.map(t => (
              <button key={t.id} type="button" onClick={() => setWahl(t.id)} className={`w-full text-left rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${t.id === wahl ? 'bg-marke/10 text-marke font-semibold' : 'hover:bg-muted'}`}>
                <span className="flex-1 truncate">{t.name}{!t.onboarding_am && <span className="block text-[10px] font-normal text-muted-foreground">Einführung offen</span>}</span>{t.ungelesen > 0 && <Badge className="bg-marke text-white text-[10px]">{t.ungelesen}</Badge>}
              </button>
            ))}
          </CardContent></Card>
          <Card className="border-border/60"><CardContent className="p-4 space-y-3">
            <div className="max-h-[55vh] overflow-y-auto pr-1">{verlauf === null ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /> : <ChatVerlauf nachrichten={verlauf} eigeneSeite="leitung" />}</div>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={5} placeholder={BEISPIEL} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" data-testid="trainer-chat-text" />
            {vorschau.length > 0 && (
              <div className="rounded-lg border border-border/60 p-2 text-xs space-y-1" data-testid="trainer-chat-vorschau">
                <p className="font-semibold">Wird in {name}s Kalender eingetragen:</p>
                {vorschau.map(v => (
                  <p key={v.nr} className={`flex gap-2 ${v.blockiert ? 'text-red-600' : v.hinweis ? 'text-amber-700 dark:text-amber-300' : ''}`}>
                    {v.blockiert || v.hinweis ? <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> : <CalendarPlus className="w-3 h-3 mt-0.5 shrink-0" />}
                    <span>{v.datum ? `${tagDE(v.datum)} ${v.start || ''}–${v.ende || ''}` : v.zeile} · {v.kunde || 'PT'}{v.blockiert ? ` — wird NICHT eingetragen: ${v.blockiert}` : v.hinweis ? ` — ${v.hinweis}` : ''}</span>
                  </p>
                ))}
              </div>
            )}
            <div className="flex justify-end"><Button onClick={senden} disabled={arbeitet || !text.trim() || !wahl} className="gap-1.5">{arbeitet ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Senden</Button></div>
          </CardContent></Card>
        </div>
      )}
    </div>
  );
}
